import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Modal } from "../ui/modal";
import DatePicker from '../form/date-picker';
import Pagination from '../ui/pagination';


export default function PromotionsCodes() {
    const [codes, setCodes] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    // Pagination & Search State
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [searchTerm, setSearchTerm] = useState('');
    const [totalItems, setTotalItems] = useState(0);

    // Form State
    const [formData, setFormData] = useState({
        code: '',
        description: '',
        amount: 0,
        percentage: 0,
        total: 0,
        qty: 0,
        active: true,
        expiration_data: ''
    });

    const refreshData = async () => {
        setLoading(true);
        try {
            // Using options.params to ensure they are sent as query parameters
            const res = await client.getApiPromotionsCodes({}, {
                params: {
                    page: currentPage,
                    search: searchTerm,
                    per_page: rowsPerPage
                }
            }) as any;

            const responseBody = res.data;
            if (responseBody?.data?.data) {
                setCodes(responseBody.data.data);
                setTotalItems(responseBody.data.total || responseBody.data.data.length);
            } else {
                const legacyData = responseBody?.data || [];
                setCodes(Array.isArray(legacyData) ? legacyData : []);
                setTotalItems(Array.isArray(legacyData) ? legacyData.length : 0);
            }
        } catch (err) {
            console.error('Failed to fetch codes:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshData();
    }, [currentPage, rowsPerPage, searchTerm]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target as any;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? Number(value) : (name === 'active' ? (value === '1') : value)
        }));
    };

    const handleEdit = (code: any) => {
        setEditingId(code.id);
        setFormData({
            code: code.code || '',
            description: code.description || '',
            amount: Number(code.amount || 0),
            percentage: Number(code.percentage || 0),
            total: Number(code.total || 0),
            qty: Number(code.qty || 0),
            active: Boolean(code.active),
            expiration_data: code.expiration_data || ''
        });
        setIsModalOpen(true);
    };

    const handleCreate = () => {
        setEditingId(null);
        setFormData({
            code: '',
            description: '',
            amount: 0,
            percentage: 0,
            total: 0,
            qty: 0,
            active: true,
            expiration_data: ''
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.expiration_data) {
            Swal.fire({
                title: 'Error',
                text: 'Expiration Date is required',
                icon: 'error'
            });
            return;
        }

        try {
            if (editingId) {
                await client.postApiPromotionsCodeUpdateId({
                    id: String(editingId),
                    postApiPromotionsCodeUpdateIdRequest: {
                        id: editingId,
                        code: formData.code,
                        description: formData.description,
                        amount: formData.amount,
                        percentage: formData.percentage,
                        total: formData.total,
                        qty: formData.qty,
                        active: formData.active,
                        expiration_data: formData.expiration_data
                    }
                });
            } else {
                await client.postApiPromotionsCodeCreate({
                    postApiPromotionsCodeCreateRequest: {
                        code: formData.code,
                        description: formData.description,
                        amount: formData.amount,
                        percentage: formData.percentage,
                        total: formData.total,
                        qty: formData.qty,
                        active: formData.active,
                        expiration_data: formData.expiration_data
                    } as any
                });
            }
            setIsModalOpen(false);
            setEditingId(null);
            refreshData();
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: editingId ? 'Coupon Code updated successfully' : 'Coupon Code created successfully',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
        } catch (err) {
            console.error('Failed to save code:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to save code',
                icon: 'error'
            });
        }
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (!result.isConfirmed) return;
        try {
            await client.postApiPromotionsCodeDelete({
                postApiPromotionsCodeDeleteRequest: { id: id }
            } as any);
            refreshData();
        } catch (err) {
            console.error('Failed to delete code:', err);
            // Fallback for different delete signature if generic object needed
            try {
                await client.postApiPromotionsCodeDelete({ id } as any);
                refreshData();
            } catch (retryErr) {
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to delete code',
                    icon: 'error'
                });
            }
        }
    };

    const totalPages = Math.ceil(totalItems / rowsPerPage);

    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
            <div className="p-5 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-white/[0.02]">
                <div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">Coupon Codes</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Manage discounts and promotional codes.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search codes..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all w-64"
                        />
                        <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    {loading && <span className="text-xs text-gray-400 italic animate-pulse">Syncing...</span>}
                    <button
                        onClick={handleCreate}
                        className="px-5 py-2.5 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-all text-sm font-bold shadow-lg shadow-brand-500/20 active:scale-95"
                    >
                        Create Code
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50/30 dark:bg-white/[0.01]">
                            <TableCell isHeader className="px-6 py-4 text-start text-xs font-black uppercase tracking-widest text-gray-400">Code</TableCell>
                            <TableCell isHeader className="px-6 py-4 text-start text-xs font-black uppercase tracking-widest text-gray-400">Discount</TableCell>
                            <TableCell isHeader className="px-6 py-4 text-start text-xs font-black uppercase tracking-widest text-gray-400">Valid Until</TableCell>
                            <TableCell isHeader className="px-6 py-4 text-start text-xs font-black uppercase tracking-widest text-gray-400">Status</TableCell>
                            <TableCell isHeader className="px-6 py-4 text-end text-xs font-black uppercase tracking-widest text-gray-400">Actions</TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {codes.length === 0 && !loading ? (
                            <TableRow>
                                <TableCell colSpan={5} className="px-6 py-12 text-center text-gray-500">
                                    <div className="flex flex-col items-center gap-2">
                                        <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                        </svg>
                                        <span className="font-medium text-lg">No codes found</span>
                                        <p className="text-sm max-w-[200px]">Start by creating your first promotional coupon code.</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            codes.map(item => (
                                <TableRow key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-white/[0.02] transition-colors">
                                    <TableCell className="px-6 py-5">
                                        <div className="flex flex-col">
                                            <span className="font-mono font-black text-brand-500 text-lg">{item.code}</span>
                                            {item.description && <span className="text-xs text-gray-400 mt-0.5">{item.description}</span>}
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-6 py-5">
                                        <div className="flex items-center gap-2">
                                            <span className="px-3 py-1 bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 rounded-lg text-sm font-bold">
                                                {item.amount > 0 ? `$${item.amount}` : `${item.percentage}%`}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-6 py-5">
                                        <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                                            {item.expiration_data || 'No Expiry'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-6 py-5">
                                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${item.active ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
                                            {item.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-6 py-5 text-end">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-all"
                                                title="Edit Code"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-all"
                                                title="Delete Code"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(rows) => {
                    setRowsPerPage(rows);
                    setCurrentPage(1);
                }}
                totalItems={totalItems}
            />

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-md">
                <div className="flex flex-col max-h-[85vh]">

                    <div className="flex justify-between items-center p-6 border-b border-gray-100 dark:border-gray-700">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-2 bg-brand-500/10 text-brand-500 rounded-lg">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                            </div>
                            {editingId ? 'Edit Coupon Code' : 'Create Coupon Code'}
                        </h3>
                        <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>

                    <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
                        <form id="couponForm" onSubmit={handleSubmit} className="space-y-5">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">
                                        Code <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="code"
                                        required
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white font-mono font-bold text-brand-500 placeholder:font-normal placeholder:text-gray-400"
                                        placeholder="SUMMER2025"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Quantity</label>
                                    <input
                                        type="number"
                                        name="qty"
                                        min="0"
                                        value={formData.qty}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Description <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="description"
                                    required
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                    placeholder="20% off all summer items"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Amount ($)</label>
                                    <input
                                        type="number"
                                        name="amount"
                                        min="0"
                                        value={formData.amount}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                        placeholder="0"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Percentage (%)</label>
                                    <input
                                        type="number"
                                        name="percentage"
                                        min="0"
                                        max="100"
                                        value={formData.percentage}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                        placeholder="0"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Expiration Date <span className="text-red-500">*</span></label>
                                    <DatePicker
                                        id="expiration_data"
                                        placeholder="Select expiration date"
                                        staticMode={false}
                                        defaultDate={formData.expiration_data ? new Date(formData.expiration_data) : undefined}
                                        onChange={(selectedDates) => {
                                            if (selectedDates.length > 0) {
                                                const d = selectedDates[0];
                                                const year = d.getFullYear();
                                                const month = String(d.getMonth() + 1).padStart(2, '0');
                                                const day = String(d.getDate()).padStart(2, '0');
                                                const val = `${year}-${month}-${day}`;
                                                setFormData(prev => ({ ...prev, expiration_data: val }));
                                            }
                                        }}
                                    />

                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Status</label>
                                    <select
                                        name="active"
                                        value={formData.active ? '1' : '0'}
                                        onChange={handleInputChange as any}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                    >
                                        <option value="1">Active</option>
                                        <option value="0">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="flex justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="px-6 py-2.5 text-gray-600 font-bold bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 dark:hover:bg-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="couponForm"
                            className="px-8 py-2.5 text-white font-bold bg-brand-500 rounded-xl hover:bg-brand-600 shadow-xl shadow-brand-500/20 active:scale-95 transition-all"
                        >
                            {editingId ? 'Update Code' : 'Create Code'}
                        </button>
                    </div>

                </div>
            </Modal>
        </div >
    );
}
